import { Navigate, Route, Routes } from "react-router-dom";
import Layout from "./components/Layout";
import { useAuth } from "./hooks/useAuth";
import AdminAuditLogsPage from "./pages/AdminAuditLogsPage";
import AdminHubPage from "./pages/AdminHubPage";
import AdminReferenceDBPage from "./pages/AdminReferenceDBPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AIModelVisualizationPage from "./pages/AIModelVisualizationPage";
import AnalysisDetailPage from "./pages/AnalysisDetailPage";
import AnalysisPage from "./pages/AnalysisPage";
import CaseDetailPage from "./pages/CaseDetailPage";
import CasesPage from "./pages/CasesPage";
import DashboardPage from "./pages/DashboardPage";
import FireDebrisWorkspacePage from "./pages/FireDebrisWorkspacePage";
import LoginPage from "./pages/LoginPage";
import ReferenceDBPage from "./pages/ReferenceDBPage";
import ReportsPage from "./pages/ReportsPage";
import SampleAnalysisPage from "./pages/SampleAnalysisPage";
import SampleDetailPage from "./pages/SampleDetailPage";
import SamplesPage from "./pages/SamplesPage";
import SearchPage from "./pages/SearchPage";
import type { UserRole } from "./types";
import { hasMinRole } from "./utils/permissions";

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

function RoleRoute({
  children,
  minimumRole,
}: {
  children: React.ReactNode;
  minimumRole: UserRole;
}) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!user || !hasMinRole(user.role, minimumRole)) {
    return <Navigate to="/" />;
  }

  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/cases" element={<CasesPage />} />
                <Route path="/cases/:id" element={<CaseDetailPage />} />
                <Route
                  path="/cases/:id/fire-debris"
                  element={<FireDebrisWorkspacePage />}
                />
                <Route path="/samples" element={<SamplesPage />} />
                <Route path="/samples/:id" element={<SampleDetailPage />} />
                <Route path="/analyses" element={<AnalysisPage />} />
                <Route path="/analyses/:id" element={<AnalysisDetailPage />} />
                <Route
                  path="/analyses/:id/dashboard"
                  element={<SampleAnalysisPage />}
                />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/reference-db" element={<ReferenceDBPage />} />
                <Route
                  path="/admin"
                  element={
                    <RoleRoute minimumRole="ADMIN">
                      <AdminHubPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <RoleRoute minimumRole="ADMIN">
                      <AdminUsersPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/ai-models"
                  element={
                    <RoleRoute minimumRole="ADMIN">
                      <AIModelVisualizationPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/audit-logs"
                  element={
                    <RoleRoute minimumRole="ADMIN">
                      <AdminAuditLogsPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/reference-db"
                  element={
                    <RoleRoute minimumRole="ADMIN">
                      <AdminReferenceDBPage />
                    </RoleRoute>
                  }
                />
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}
